import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import type { GroupWithMembers, IhqType, WorkbenchUser } from "../types";
import { IHQ_TYPES, isGroupLocked } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { useGroupStore } from "../store/groupStore";
import { notify } from "../store/uiStore";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { FieldRow } from "../components/FieldRow";
import { ErrorState, SkeletonForm } from "../components/ScreenStates";
import { ButtonGroupInput, DateInput, SelectInput, TextArea, TextInput } from "../components/fields";
import { hasErrors, isoDate, required, validateFields, type FieldErrors } from "../validation/rules";

interface GroupEditValues {
  name: string;
  effectiveDate: string;
  /** Null while a migrated group's type is still Unknown; the form requires a choice before saving. */
  ihqType: IhqType | null;
  notes: string;
  underwriterUserId: string;
}

type GroupEditField = keyof GroupEditValues;

const RULES = {
  name: [required("Group Name is required")],
  effectiveDate: [required("Effective Date is required"), isoDate()],
  ihqType: [required()],
  notes: [],
  underwriterUserId: [],
} as const;

const IHQ_OPTIONS = IHQ_TYPES.map((value) => ({ value, label: value }));

interface GroupEditFormProps {
  group: GroupWithMembers;
  users: readonly WorkbenchUser[];
}

function GroupEditForm({ group, users }: GroupEditFormProps) {
  const navigate = useNavigate();
  const updateGroup = useGroupStore((state) => state.updateGroup);
  const [values, setValues] = useState<GroupEditValues>({ name: group.name, effectiveDate: group.effectiveDate, ihqType: group.ihqType === "Unknown" ? null : group.ihqType, notes: group.notes ?? "", underwriterUserId: group.underwriterUserId ?? "" });
  const [errors, setErrors] = useState<FieldErrors<GroupEditField>>({});
  const [isSaving, setIsSaving] = useState(false);
  const underwriterOptions = useMemo(() => [{ value: "", label: "Unassigned" }, ...users.map((user) => ({ value: user.id, label: user.displayName }))], [users]);
  const ihqChanged = values.ihqType !== null && values.ihqType !== group.ihqType;
  const typeIsUnknown = group.ihqType === "Unknown";

  const handleSave = useCallback(async () => {
    const nextErrors = validateFields<GroupEditField>(values, RULES);
    setErrors(nextErrors);
    if (hasErrors(nextErrors) || values.ihqType === null) return;
    setIsSaving(true);
    const saved = await updateGroup({ name: values.name.trim(), effectiveDate: values.effectiveDate, ihqType: values.ihqType, notes: values.notes.trim() || null, underwriterUserId: values.underwriterUserId || null });
    setIsSaving(false);
    if (saved) {
      notify(`Saved ${values.name.trim()}.${ihqChanged ? " RMM was recomputed against the active " + values.ihqType + " table." : ""}`, "success");
      navigate(`/uw/groups/${group.id}`);
    }
  }, [values, updateGroup, ihqChanged, navigate, group.id]);

  const handleCancel = useCallback(() => navigate(`/uw/groups/${group.id}`), [navigate, group.id]);
  const shortcuts = useMemo(() => ({ save: () => void handleSave(), cancel: handleCancel }), [handleSave, handleCancel]);
  useShortcuts(shortcuts);

  return (
    <div className="mx-auto max-w-[720px] space-y-5">
      <Card title="Group Details" icon={FileText}>
        <div className="space-y-4">
          <TextInput id="edit-group-name" label="Group Name" value={values.name} onChange={(name) => setValues({ ...values, name })} error={errors.name} required maxLength={120} />
          <FieldRow>
            <DateInput id="edit-group-effective" label="Effective Date" value={values.effectiveDate} onChange={(effectiveDate) => setValues({ ...values, effectiveDate })} error={errors.effectiveDate} required firstOfMonthOnly hint="Ages and build points recompute as of this date." />
            <ButtonGroupInput id="edit-group-ihq" label="IHQ Type" value={values.ihqType} onChange={(ihqType) => setValues({ ...values, ihqType })} options={IHQ_OPTIONS} required hint={typeIsUnknown ? "Unknown since migration; the math has used the Long table. Choose the real form type." : "Selects the active Expected Debits Table."} />
          </FieldRow>
          {ihqChanged && <Alert type="warning" message={`Switching to the ${values.ihqType} form changes the Expected Debits Table and will move RMM.`} />}
          {group.hasSrxCarveOut && <Alert type="info" message="Specialty Rx carve-out is on for this group. It comes from the latest Quote version in Salesforce and is not edited here." />}
          <SelectInput id="edit-group-underwriter" label="Underwriter" value={values.underwriterUserId} onChange={(underwriterUserId) => setValues({ ...values, underwriterUserId })} options={underwriterOptions} />
          <TextArea id="edit-group-notes" label="Notes" value={values.notes} onChange={(notes) => setValues({ ...values, notes })} />
        </div>
      </Card>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} isLoading={isSaving}>
          Save Group
        </Button>
      </div>
    </div>
  );
}

export function EditGroupPage() {
  const { groupId = "" } = useParams();
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const users = useAsyncResource(() => getApi().listUsers(), "users", "EditGroupPage.users");
  const breadcrumb = useMemo(() => (group ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: "Edit Group", to: "" }] : null), [group]);
  usePageSetup("Edit Group", breadcrumb);

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;
  if (!group || !users.data) return <SkeletonForm fields={6} />;
  if (isGroupLocked(group)) return <ErrorState message="This version is read-only. Reopen it as a new version to make changes." onRetry={ensure.retry} />;

  return (
    <>
      <PageHeader title="Edit Group" subtitle={`${group.name} v${group.version}`} />
      <GroupEditForm key={group.id} group={group} users={users.data} />
    </>
  );
}
